import { FC, useRef, useState } from 'react'
import { Form, Outlet, useMatch, useNavigate } from 'react-router-dom'
import './pm.css'

// COMPONENTS
import { Column } from '@components/templates/column'
import AnchorLink from '@components/atoms/anchor-link'
import PaymentMethodCell from '@components/organisims/payment-method-cell'
import Button from '@components/atoms/button'

// ROUTES
import AddCardRoute from './add'
import EditCardRoute from './edit'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { useUser } from '@clerk/clerk-react'
import { createResponseObj, StripeAPI } from '@apis'
import { isNonEmptyString } from '@utils/type-checkers'
import { createAction } from '@utils/actions'
import { useInput } from '@hooks/inputs'
import { usePaymentMethods } from '@/hooks/tanstack/usePaymentMethods'
import { queryClient } from '@/lib/queryClient'

const Component: FC<{}> = () => {
    const { user } = useUser()
    const { data: paymentMethods = [] } = usePaymentMethods(user?.id)
    const navigate = useNavigate()
    const ref = useRef<HTMLDialogElement>(null)

    const isAdd = useMatch({ path: '/account/pm/add' })
    const isEdit = useMatch({ path: '/account/pm/:token/edit' })
    const isDefault = !isAdd && !isEdit

    const [_, sigTokenProps, setSigToken] = useInput()

    function promptRemove(token: string) {
        console.log(token)
        setSigToken(token)
        if (ref.current) ref.current.showModal()
    }
    function closePrompt() {
        setSigToken('')
        if (ref.current) ref.current.close()
    }

    function handleAdd() {
        navigate('./add')
    }

    function handleBack() {
        navigate(-1)
    }

    return (
        <Column.Content className="account-pm-screen">
            {(isEdit) && (
                <a onClick={handleBack}>
                    <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
                </a>
            )}

            {/* Titles */}
            {isAdd && <h3 id='add-card-title' style={{ textAlign: 'center' }}>Add New Payment Method</h3>}
            {isEdit && <h3 style={{ textAlign: 'center' }}>Update Payment Method</h3>}
            {isDefault && <h3 style={{ textAlign: 'center' }}>Saved Payment Methods</h3>}
            {(isAdd || isDefault) && (
                <PaymentMethodCell type={isDefault ? 'add' : 'empty'} onClick={!isAdd ? handleAdd : undefined}>
                    <Outlet />
                </PaymentMethodCell>
            )}
            {(!isDefault) ? null : paymentMethods.map((method, index) => (
                <EditablePMCell key={`${method.token}-${index}`} promptRemove={promptRemove} editable={true} paymentMethod={method} />
            ))}
            {(isEdit) && <Outlet />}
            {!isDefault ? null : <Column.Footer hidden />}
            <dialog ref={ref}>
                <div>
                    <Form className="flex flex-col items-stretch" onSubmit={closePrompt} style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 4rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <h2 style={{ textAlign: 'center' }}>Wait!</h2>
                        <p style={{ textAlign: 'center' }}>Are you sure you want to remove this payment method?</p>
                        <input {...sigTokenProps} name="token" hidden />
                        <div className='flex flex-row gap-2.5'>
                            <Button theme="secondary" style={{ flex: 1 }} formMethod="dialog" onClick={closePrompt}>Cancel</Button>
                            <Button theme="primary" style={{ flex: 1 }} type="submit" formMethod="POST">Remove Payment</Button>
                        </div>
                    </Form>
                </div>
            </dialog>
        </Column.Content>
    )
}

const EditablePMCell: FC<React.ComponentProps<typeof PaymentMethodCell> & { promptRemove: (token: string) => void }> = (props) => {
    const { paymentMethod, promptRemove, ...pmcProps } = props
    const [showRemove, setShowRemove] = useState(false)

    function toggleEdit() {
        setShowRemove(!showRemove)
    }

    return (<>
        <PaymentMethodCell paymentMethod={paymentMethod} {...pmcProps} style={{ border: showRemove ? '2px solid var(--splitcard-blue-500)' : '1px solid var(--splitcard-gray-200)' }} onEditClick={toggleEdit}>
            {(showRemove) && (<>
                {(paymentMethod?.type === 'credit_card') && <Button theme="primary" to={`./${paymentMethod?.token}/edit`} style={{ cursor: 'pointer', pointerEvents: 'auto' }}>Update Card Details</Button>}
                {(!!paymentMethod && 'token' in paymentMethod) && <AnchorLink padding={true} onClick={() => { promptRemove(paymentMethod?.token || '') }} style={{ alignSelf: 'center', color: 'var(--splitcard-red-500)', cursor: 'pointer', pointerEvents: 'auto'}}>Remove this card</AnchorLink>}
            </>)}
        </PaymentMethodCell>
    </>)
}

const action = createAction(async ({ request }) => {
    const data = await request.formData()
    const token = data.get('token')
    const userId = window.Clerk?.user?.id

    if (!isNonEmptyString(token) || !isNonEmptyString(userId)) throw createResponseObj({
        status: 'failed', type: '', name: '',
        message: `Invalid Form Data: ${JSON.stringify({ token, userId })}`
    })

    await StripeAPI.RemovePaymentMethodRequest.fetch(token)
    queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
    return null
})

const PMRoute: RouteObject = {
    id: 'payment-methods',
    path: "pm",
    Component,
    action,
    children: [
        AddCardRoute,
        EditCardRoute
    ],
}

export { PMRoute }
